import React, { useState, useEffect, useRef } from 'react';
import { Shield, Check, X, LogOut, RefreshCw, User, Users, Leaf, GitMerge, AlertTriangle, Play, Tag, Download, Megaphone, MessageCircleQuestion, Sprout } from 'lucide-react';
import AdminEditorialContent from './components/AdminEditorialContent';
import AdminLandscapingQA from './components/AdminLandscapingQA';
import AdminGardeners from './components/AdminGardeners';
import { API_BASE_URL } from './config';
import UserManagement from './UserManagement';

function designerAcceptingFromProfile(profileData) {
  if (profileData == null || profileData === '') return true;
  let o = profileData;
  if (typeof profileData === 'string') {
    try {
      o = JSON.parse(profileData);
    } catch {
      return true;
    }
  }
  if (typeof o !== 'object' || !o) return true;
  return o.accepting_new_projects !== false;
}

// Bulk Attribute Scan Component
function BulkAttributeScan() {
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [downloadingPlantList, setDownloadingPlantList] = useState(false);
  const [attributes, setAttributes] = useState('deer resistant, drought tolerant, pollinator friendly, low maintenance, evergreen, deciduous, fragrant, native, red, pink, purple, blue, yellow, orange, white, green, multicolor');
  
  // Data verification state
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyFields, setVerifyFields] = useState(['category', 'zone']);

  const handleDownloadPlantList = async (format = 'csv') => {
    setDownloadingPlantList(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No admin token found');
        return;
      }

      const url = format === 'csv'
        ? `${API_BASE_URL}/plants/export-botanical-names?format=csv&layout=attribute_rows`
        : `${API_BASE_URL}/plants/export-botanical-names?format=${encodeURIComponent(format)}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Download failed (${response.status})`);
      }

      if (format === 'json') {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = 'plant-botanical-names.json';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
        return;
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = 'plant-attribute-review-sheet.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading plant list:', error);
      alert(`Failed to download plant list: ${error.message || 'Network error'}`);
    } finally {
      setDownloadingPlantList(false);
    }
  };

  const handleBulkScan = async () => {
    if (!window.confirm('This will scan all plants in the database for the specified attributes. This may take several minutes. Continue?')) {
      return;
    }

    setScanning(true);
    setScanResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No admin token found');
        return;
      }

      // Parse attributes from comma-separated string
      const attributesArray = attributes.split(',').map(a => a.trim()).filter(a => a.length > 0);

      if (attributesArray.length === 0) {
        alert('Please enter at least one attribute');
        setScanning(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/plants/bulk-attribute-scan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes: attributesArray }),
      });

      if (response.ok) {
        const data = await response.json();
        setScanResult(data);
        alert(`Bulk scan completed!\n\nTotal plants: ${data.total_plants}\nScanned: ${data.scanned}\nReviews created: ${data.reviews_created || 0}\n\nCheck the Attribute Reviews tab to review suggestions.`);
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        alert(`Failed to run bulk scan: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error running bulk scan:', error);
      alert(`Failed to run bulk scan: ${error.message || 'Network error'}`);
    } finally {
      setScanning(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Attributes to Scan (comma-separated)
        </label>
        <input
          type="text"
          value={attributes}
          onChange={(e) => setAttributes(e.target.value)}
          placeholder="deer resistant, drought tolerant, pollinator friendly"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={scanning}
        />
        <p className="mt-1 text-xs text-gray-500">
          Common attributes: deer resistant, drought tolerant, pollinator friendly, low maintenance, evergreen, deciduous, fragrant, native
        </p>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          onClick={handleBulkScan}
          disabled={scanning}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
        >
          {scanning ? (
            <>
              <RefreshCw size={16} className="animate-spin" />
              Scanning...
            </>
          ) : (
            <>
              <Play size={16} />
              Run Bulk Scan
            </>
          )}
        </button>

        <button
          onClick={() => handleDownloadPlantList('csv')}
          disabled={downloadingPlantList}
          className="px-4 py-2 bg-white border border-blue-300 hover:bg-blue-50 text-blue-700 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          title="Download a spreadsheet-ready CSV: one row per plant+attribute with evidence + links"
        >
          {downloadingPlantList ? (
            <>
              <RefreshCw size={16} className="animate-spin" />
              Downloading...
            </>
          ) : (
            <>
              <Download size={16} />
              Download Review Sheet (CSV)
            </>
          )}
        </button>

        <button
          onClick={() => handleDownloadPlantList('json')}
          disabled={downloadingPlantList}
          className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          title="Download a JSON export of all plants (includes id + current attributes)"
        >
          <Download size={16} />
          JSON
        </button>
      </div>

      {scanResult && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
          <p className="text-sm text-green-800">
            <strong>Scan Complete!</strong><br />
            Total plants: {scanResult.total_plants}<br />
            Scanned: {scanResult.scanned}<br />
            Reviews created: {scanResult.reviews_created || 0}
          </p>
        </div>
      )}
      
      <div className="mt-6 pt-6 border-t border-blue-300">
        <h4 className="text-md font-semibold text-gray-900 mb-3">Verify Database Fields</h4>
        <p className="text-sm text-gray-600 mb-4">
          Use AI to verify that category and zone information in the database is correct. 
          Discrepancies will be flagged for review in the Data Corrections tab.
        </p>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Fields to Verify
          </label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={verifyFields.includes('category')}
                onChange={(e) => {
                  if (e.target.checked) {
                    setVerifyFields([...verifyFields, 'category']);
                  } else {
                    setVerifyFields(verifyFields.filter(f => f !== 'category'));
                  }
                }}
                disabled={verifying}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Category</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={verifyFields.includes('zone')}
                onChange={(e) => {
                  if (e.target.checked) {
                    setVerifyFields([...verifyFields, 'zone']);
                  } else {
                    setVerifyFields(verifyFields.filter(f => f !== 'zone'));
                  }
                }}
                disabled={verifying}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Hardiness Zone</span>
            </label>
          </div>
        </div>
        
        <button
          onClick={async () => {
            if (!window.confirm('This will verify all plants for the selected fields. This may take several minutes. Continue?')) {
              return;
            }

            setVerifying(true);
            setVerifyResult(null);

            try {
              const token = localStorage.getItem('adminToken');
              if (!token) {
                alert('No admin token found');
                return;
              }

              if (verifyFields.length === 0) {
                alert('Please select at least one field to verify');
                setVerifying(false);
                return;
              }

              const response = await fetch(`${API_BASE_URL}/plants/bulk-verify-data`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ fields: verifyFields }),
              });

              if (response.ok) {
                const data = await response.json();
                setVerifyResult(data);
                alert(`Verification completed!\n\nTotal plants: ${data.total_plants}\nVerified: ${data.verified}\nDiscrepancies found: ${data.discrepancies_found}\n\nCheck the Data Corrections tab to review any discrepancies.`);
              } else {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                alert(`Failed to run verification: ${errorData.error || 'Unknown error'}`);
              }
            } catch (error) {
              console.error('Error running verification:', error);
              alert(`Failed to run verification: ${error.message || 'Network error'}`);
            } finally {
              setVerifying(false);
            }
          }}
          disabled={verifying || verifyFields.length === 0}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
        >
          {verifying ? (
            <>
              <RefreshCw size={16} className="animate-spin" />
              Verifying... (This may take several minutes)
            </>
          ) : (
            <>
              <Play size={16} />
              Verify Database Fields
            </>
          )}
        </button>

        {verifyResult && (
          <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded">
            <p className="text-sm text-purple-800">
              <strong>Verification Complete!</strong><br />
              Total plants: {verifyResult.total_plants}<br />
              Verified: {verifyResult.verified}<br />
              Discrepancies found: {verifyResult.discrepancies_found}<br />
              {verifyResult.discrepancies_found > 0 && (
                <span className="font-semibold">Check the Data Corrections tab to review!</span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard({ onLogout }) {
  const [activeTab, setActiveTab] = useState('users'); // 'users', 'plants', 'designers', 'corrections', 'attributes', 'editorial', 'landscaping-qa'
  const [loading, setLoading] = useState(false); // UserManagement handles its own loading
  const [processing, setProcessing] = useState(false);
  const [adminData, setAdminData] = useState(null);
  
  // User management state
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  
  // Pending plants state
  const [pendingPlants, setPendingPlants] = useState([]);
  const [approvedPlants, setApprovedPlants] = useState([]);
  const [plantsViewMode, setPlantsViewMode] = useState('pending'); // 'pending' or 'approved'
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [plantReviewNotes, setPlantReviewNotes] = useState('');
  const [mergeIntoPlantId, setMergeIntoPlantId] = useState('');
  const [makePublic, setMakePublic] = useState(true);
  
  // Designers management state
  const [designers, setDesigners] = useState([]);
  const [loadingDesigners, setLoadingDesigners] = useState(false);
  
  // Data corrections state
  const [dataCorrections, setDataCorrections] = useState([]);
  const [loadingCorrections, setLoadingCorrections] = useState(false);
  const [resolvingCorrection, setResolvingCorrection] = useState(null);
  const [selectedCorrections, setSelectedCorrections] = useState(new Set());
  const [correctionFilter, setCorrectionFilter] = useState('all'); // 'all', 'category', 'hardiness_zones'
  
  // Attribute reviews state
  const [attributeReviews, setAttributeReviews] = useState([]);
  const [loadingAttributeReviews, setLoadingAttributeReviews] = useState(false);
  const [resolvingAttributeReview, setResolvingAttributeReview] = useState(null);
  const [selectedAttributeReviews, setSelectedAttributeReviews] = useState(new Set());
  const [attributeReviewFilter, setAttributeReviewFilter] = useState('all'); // 'all' or specific attribute
  const [attributeReviewStats, setAttributeReviewStats] = useState({});
  const [enrichingAttributeReviews, setEnrichingAttributeReviews] = useState(false);
  const [enrichingExternalAttributeReviews, setEnrichingExternalAttributeReviews] = useState(false);
  const [autoEnrichingExternalAttributeReviews, setAutoEnrichingExternalAttributeReviews] = useState(false);
  const [autoEnrichExternalProgress, setAutoEnrichExternalProgress] = useState({ batches: 0, processed: 0, updated: 0, lastMessage: '' });
  const stopAutoEnrichExternalRef = useRef(false);
  const [attributeReviewEvidenceFilter, setAttributeReviewEvidenceFilter] = useState('all'); // all|verified|ai_cache|external|external_supports|external_inat|external_powo|none
  const [attributeReviewGroupBy, setAttributeReviewGroupBy] = useState('plant'); // plant|attribute

  function renderTextWithLinks(text) {
    const str = String(text || '');
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return str.split('\n').map((line, lineIdx) => {
      const parts = line.split(urlRegex);
      return (
        <div key={lineIdx}>
          {parts.map((part, idx) => {
            if (/^https?:\/\//.test(part)) {
              return (
                <a
                  key={`${lineIdx}-${idx}`}
                  href={part}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-700 underline break-all"
                >
                  {part}
                </a>
              );
            }
            return <span key={`${lineIdx}-${idx}`}>{part}</span>;
          })}
        </div>
      );
    });
  }

  function safeParseEvidence(sourceEvidence) {
    if (!sourceEvidence) return null;
    if (typeof sourceEvidence === 'object') return sourceEvidence;
    try {
      return JSON.parse(sourceEvidence);
    } catch {
      return null;
    }
  }

  function getEvidenceFlagsFromReview(review) {
    const evidence = safeParseEvidence(review?.source_evidence);
    const supportedRaw = evidence && typeof evidence === 'object' ? evidence.supported : null;
    const verifiedSupported = supportedRaw === true || supportedRaw === 'true';
    const rule = evidence && typeof evidence === 'object' ? (evidence.rule || '') : '';
    const aiCacheOnly = !verifiedSupported && typeof rule === 'string' && rule.toLowerCase().startsWith('ai cache');

    const external = evidence && typeof evidence === 'object' ? evidence.external : null;
    const externalPresent = !!external;
    const externalSupportsRaw = external?.attribute_check?.supported;
    const externalSupports = externalSupportsRaw === true;
    const hasINat = !!external?.sources?.inaturalist;
    const hasPowo = !!external?.sources?.powo;

    return { verifiedSupported, aiCacheOnly, externalPresent, externalSupports, hasINat, hasPowo };
  }

  function getPlantEvidenceBadges(reviews) {
    const flags = (Array.isArray(reviews) ? reviews : []).map(getEvidenceFlagsFromReview);
    const anyVerified = flags.some(f => f.verifiedSupported);
    const anyAiCache = flags.some(f => f.aiCacheOnly);
    const anyExternalSupports = flags.some(f => f.externalSupports);
    const anyExternalPresent = flags.some(f => f.externalPresent);
    const anyINat = flags.some(f => f.hasINat);
    const anyPowo = flags.some(f => f.hasPowo);

    const badges = [];
    if (anyVerified) badges.push({ key: 'verified', label: 'Verified', className: 'bg-green-100 text-green-800 border-green-200' });
    if (anyExternalSupports) badges.push({ key: 'external_supports', label: `External supports${anyINat || anyPowo ? ` (${[anyINat ? 'iNat' : null, anyPowo ? 'POWO' : null].filter(Boolean).join(', ')})` : ''}`, className: 'bg-blue-100 text-blue-800 border-blue-200' });
    else if (anyExternalPresent) badges.push({ key: 'external_present', label: `External present${anyINat || anyPowo ? ` (${[anyINat ? 'iNat' : null, anyPowo ? 'POWO' : null].filter(Boolean).join(', ')})` : ''}`, className: 'bg-slate-100 text-slate-800 border-slate-200' });
    if (!anyVerified && anyAiCache) badges.push({ key: 'ai_cache', label: 'AI cache', className: 'bg-amber-100 text-amber-900 border-amber-200' });
    if (badges.length === 0) badges.push({ key: 'none', label: 'No evidence yet', className: 'bg-gray-100 text-gray-700 border-gray-200' });
    return badges;
  }

  useEffect(() => {
    const data = localStorage.getItem('adminData');
    if (data) {
      setAdminData(JSON.parse(data));
    }
    // Load data for the active tab
    if (activeTab === 'plants') {
      if (plantsViewMode === 'pending') {
        fetchPendingPlants();
      } else {
        fetchApprovedPlants();
      }
    } else if (activeTab === 'designers') {
      fetchDesigners();
    } else if (activeTab === 'attributes') {
      fetchAttributeReviews();
    } else if (activeTab === 'corrections') {
      fetchDataCorrections();
    } else if (activeTab === 'editorial') {
      setLoading(false);
    } else if (activeTab === 'landscaping-qa') {
      setLoading(false);
    } else if (activeTab === 'users') {
      // UserManagement component handles its own loading
      setLoading(false);
    } else {
      // If no tab is active, set loading to false
      setLoading(false);
    }
  }, [activeTab, plantsViewMode]);

  async function loadAllUsers() {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        console.error('No admin token found');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else if (response.status === 401 || response.status === 403) {
        alert('Session expired. Please login again.');
        if (onLogout) {
          onLogout();
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Error loading users:', errorData);
        alert(`Failed to load users: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      alert(`Failed to load users: ${error.message || 'Network error'}`);
    } finally {
      setLoading(false);
    }
  }

  // User Management Functions
  async function fetchUsers(search = '') {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const url = search
        ? `${API_BASE_URL}/admin/users?search=${encodeURIComponent(search)}`
        : `${API_BASE_URL}/admin/users`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      } else if (response.status === 401 || response.status === 403) {
        alert('Session expired. Please login again.');
        onLogout();
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  async function updateUser(userId, updates) {
    setProcessing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (response.ok) {
        alert('User updated successfully');
        setEditingUser(null);
        fetchUsers(searchQuery);
      } else {
        const error = await response.json();
        alert(`Failed to update user: ${error.error}`);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Error updating user');
    } finally {
      setProcessing(false);
    }
  }

  async function resetUserPassword(userId, password) {
    setProcessing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newPassword: password }),
      });

      if (response.ok) {
        alert('Password reset successfully');
        setResetPasswordUser(null);
        setNewPassword('');
      } else {
        const error = await response.json();
        alert(`Failed to reset password: ${error.error}`);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Error resetting password');
    } finally {
      setProcessing(false);
    }
  }

  async function deleteUser(userId) {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        alert('User deleted successfully');
        fetchUsers(searchQuery);
      } else {
        alert('Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error deleting user');
    } finally {
      setProcessing(false);
    }
  }

  async function fetchPendingPlants() {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      // Use dedicated pending plants endpoint
      const response = await fetch(`${API_BASE_URL}/admin/pending-plants`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Backend returns array directly or wrapped in plants property
        const pending = Array.isArray(data) ? data : (data.plants || []);
        setPendingPlants(pending);
      } else if (response.status === 401 || response.status === 403) {
        alert('Session expired. Please login again.');
        onLogout();
      }
    } catch (error) {
      console.error('Error fetching pending plants:', error);
      alert('Failed to load pending plants');
    } finally {
      setLoading(false);
    }
  }

  async function approvePlant(plantId) {
    if (!window.confirm('Approve this plant and add it to the public database?')) {
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/plants/${plantId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'approved',
          is_public: makePublic,
          admin_notes: plantReviewNotes || 'Approved by admin'
        }),
      });

      if (response.ok) {
        alert('Plant approved and added to database!');
        setSelectedPlant(null);
        setPlantReviewNotes('');
        setMakePublic(true);
        fetchPendingPlants();
      } else {
        const error = await response.json();
        alert(`Failed to approve plant: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error approving plant:', error);
      alert('Error approving plant');
    } finally {
      setProcessing(false);
    }
  }

  async function mergePlant(plantId) {
    if (!mergeIntoPlantId.trim()) {
      alert('Please enter a plant ID to merge into');
      return;
    }

    if (!window.confirm(`Merge this pending plant into plant ID ${mergeIntoPlantId}?`)) {
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/plants/${plantId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'merged',
          merged_into_plant_id: parseInt(mergeIntoPlantId),
          admin_notes: plantReviewNotes || 'Merged by admin'
        }),
      });

      if (response.ok) {
        alert('Plant merged successfully!');
        setSelectedPlant(null);
        setPlantReviewNotes('');
        setMergeIntoPlantId('');
        fetchPendingPlants();
      } else {
        const error = await response.json();
        alert(`Failed to merge plant: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error merging plant:', error);
      alert('Error merging plant');
    } finally {
      setProcessing(false);
    }
  }

  async function rejectPlant(plantId) {
    if (!window.confirm('Reject this plant submission? It will be removed from pending list.')) {
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/plants/${plantId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'rejected',
          admin_notes: plantReviewNotes || 'Rejected by admin'
        }),
      });

      if (response.ok) {
        alert('Plant rejected and removed from pending list');
        setSelectedPlant(null);
        setPlantReviewNotes('');
        fetchPendingPlants();
      } else {
        const error = await response.json();
        alert(`Failed to reject plant: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error rejecting plant:', error);
      alert('Error rejecting plant');
    } finally {
      setProcessing(false);
    }
  }

  async function fetchApprovedPlants() {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      // Fetch approved public plants
      const response = await fetch(`${API_BASE_URL}/plants?is_public=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Backend returns array directly or wrapped in plants property
        const approved = Array.isArray(data) ? data : (data.plants || []);
        setApprovedPlants(approved.filter(p => p.is_public === true));
      } else if (response.status === 401 || response.status === 403) {
        alert('Session expired. Please login again.');
        onLogout();
      }
    } catch (error) {
      console.error('Error fetching approved plants:', error);
      alert('Failed to load approved plants');
    } finally {
      setLoading(false);
    }
  }

  async function disablePlant(plantId) {
    if (!window.confirm('Disable this plant from the public database? It will be removed from public view but remain in the database.')) {
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/plants/${plantId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          is_public: false,
          admin_notes: plantReviewNotes || 'Disabled by admin'
        }),
      });

      if (response.ok) {
        alert('Plant disabled and removed from public view');
        setSelectedPlant(null);
        setPlantReviewNotes('');
        fetchApprovedPlants();
      } else {
        const error = await response.json();
        alert(`Failed to disable plant: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error disabling plant:', error);
      alert('Error disabling plant');
    } finally {
      setProcessing(false);
    }
  }

  async function fetchDesigners() {
    setLoadingDesigners(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/designers/admin/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDesigners(data.designers || []);
      } else if (response.status === 401 || response.status === 403) {
        alert('Session expired. Please login again.');
        onLogout();
      }
    } catch (error) {
      console.error('Error fetching designers:', error);
      alert('Failed to load designers');
    } finally {
      setLoadingDesigners(false);
    }
  }

  async function toggleDesignerActive(designerId, currentStatus) {
    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this designer's public listing?`)) {
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/designers/${designerId}/active-status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isActivePublic: !currentStatus }),
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Designer ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        fetchDesigners();
      } else {
        const error = await response.json();
        alert(`Failed to update designer: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating designer:', error);
      alert('Error updating designer status');
    } finally {
      setProcessing(false);
    }
  }

  async function toggleDesignerAcceptingProjects(designerId, currentlyAccepting) {
    setProcessing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/designers/${designerId}/accepting-new-projects`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ acceptingNewProjects: !currentlyAccepting }),
      });
      if (response.ok) {
        fetchDesigners();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.error || 'Failed to update accepting status');
      }
    } catch (e) {
      console.error(e);
      alert('Failed to update accepting status');
    } finally {
      setProcessing(false);
    }
  }

  // Data Corrections Functions
  async function fetchDataCorrections() {
    setLoadingCorrections(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        console.error('No admin token found');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/plants/data-corrections`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setDataCorrections(data.corrections || []);
      } else if (response.status === 401 || response.status === 403) {
        alert('Session expired. Please login again.');
        if (onLogout) {
          onLogout();
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Error loading corrections:', errorData);
        alert(`Failed to load data corrections: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error loading corrections:', error);
      alert(`Failed to load data corrections: ${error.message || 'Network error'}`);
    } finally {
      setLoadingCorrections(false);
    }
  }
  
  function toggleCorrectionSelection(correctionId) {
    const newSelection = new Set(selectedCorrections);
    if (newSelection.has(correctionId)) {
      newSelection.delete(correctionId);
    } else {
      newSelection.add(correctionId);
    }
    setSelectedCorrections(newSelection);
  }
  
  function toggleSelectAll() {
    const filteredCorrections = correctionFilter === 'all' 
      ? dataCorrections 
      : dataCorrections.filter(c => c.field === correctionFilter);
    
    const filteredIds = new Set(filteredCorrections.map(c => c.id));
    const allSelected = filteredIds.size > 0 && Array.from(filteredIds).every(id => selectedCorrections.has(id));
    
    if (allSelected) {
      // Deselect all filtered items
      const newSelection = new Set(selectedCorrections);
      filteredIds.forEach(id => newSelection.delete(id));
      setSelectedCorrections(newSelection);
    } else {
      // Select all filtered items
      const newSelection = new Set(selectedCorrections);
      filteredIds.forEach(id => newSelection.add(id));
      setSelectedCorrections(newSelection);
    }
  }
  
  async function resolveBulkCorrections(correctionIds, action, applied = false) {
    const count = correctionIds.length;
    const actionText = action === 'accept' ? (applied ? 'accept and apply' : 'accept') : 'reject';
    if (!window.confirm(`Are you sure you want to ${actionText} ${count} correction(s)?${applied ? ' This will update the database.' : ''}`)) {
      return;
    }
    
    setResolvingCorrection('bulk');
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No admin token found');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/plants/data-corrections/bulk-resolve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ correctionIds, action, applied }),
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`Successfully ${action === 'accept' ? 'accepted' : 'rejected'} ${data.resolved || count} correction(s)!`);
        // Refresh corrections list
        fetchDataCorrections();
        setSelectedCorrections(new Set()); // Clear selection
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        alert(`Failed to resolve corrections: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error resolving bulk corrections:', error);
      alert(`Failed to resolve corrections: ${error.message || 'Network error'}`);
    } finally {
      setResolvingCorrection(null);
    }
  }
  
  async function resolveCorrection(correctionId, action, applied = false) {
    if (!window.confirm(`Are you sure you want to ${action} this correction?${applied ? ' This will update the database.' : ''}`)) {
      return;
    }
    
    setResolvingCorrection(correctionId);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No admin token found');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/plants/data-corrections/${correctionId}/resolve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action, applied }),
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`Correction ${action === 'accept' ? 'accepted' : 'rejected'} successfully!`);
        // Refresh corrections list
        fetchDataCorrections();
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        alert(`Failed to resolve correction: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error resolving correction:', error);
      alert(`Failed to resolve correction: ${error.message || 'Network error'}`);
    } finally {
      setResolvingCorrection(null);
    }
  }

  // Attribute Reviews Functions
  async function fetchAttributeReviews(evidenceOverride = null) {
    setLoadingAttributeReviews(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        console.error('No admin token found');
        return;
      }

      const effectiveEvidence = evidenceOverride ?? attributeReviewEvidenceFilter;
      const evidenceParam = effectiveEvidence && effectiveEvidence !== 'all'
        ? `?evidence=${encodeURIComponent(effectiveEvidence)}`
        : '';

      const response = await fetch(`${API_BASE_URL}/plants/attribute-reviews${evidenceParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setAttributeReviews(data.reviews || []);
        setAttributeReviewStats(data.stats || {});
      } else if (response.status === 401 || response.status === 403) {
        alert('Session expired. Please login again.');
        if (onLogout) {
          onLogout();
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Error loading attribute reviews:', errorData);
        alert(`Failed to load attribute reviews: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error loading attribute reviews:', error);
      alert(`Failed to load attribute reviews: ${error.message || 'Network error'}`);
    } finally {
      setLoadingAttributeReviews(false);
    }
  }
  
  function toggleAttributeReviewSelection(reviewId) {
    const newSelection = new Set(selectedAttributeReviews);
    if (newSelection.has(reviewId)) {
      newSelection.delete(reviewId);
    } else {
      newSelection.add(reviewId);
    }
    setSelectedAttributeReviews(newSelection);
  }
  
  function toggleSelectAllAttributeReviews() {
    const filteredReviews = attributeReviewFilter === 'all' 
      ? attributeReviews 
      : attributeReviews.filter(r => r.attribute === attributeReviewFilter);
    
    const filteredIds = new Set(filteredReviews.map(r => r.id));
    const allSelected = filteredIds.size > 0 && Array.from(filteredIds).every(id => selectedAttributeReviews.has(id));
    
    if (allSelected) {
      // Deselect all filtered items
      const newSelection = new Set(selectedAttributeReviews);
      filteredIds.forEach(id => newSelection.delete(id));
      setSelectedAttributeReviews(newSelection);
    } else {
      // Select all filtered items
      const newSelection = new Set(selectedAttributeReviews);
      filteredIds.forEach(id => newSelection.add(id));
      setSelectedAttributeReviews(newSelection);
    }
  }

  function toggleSelectAttributeReviewIds(reviewIds) {
    const ids = Array.isArray(reviewIds) ? reviewIds.filter(Boolean) : [];
    if (ids.length === 0) return;
    const next = new Set(selectedAttributeReviews);
    const allSelected = ids.every(id => next.has(id));
    if (allSelected) {
      ids.forEach(id => next.delete(id));
    } else {
      ids.forEach(id => next.add(id));
    }
    setSelectedAttributeReviews(next);
  }
  
  async function resolveBulkAttributeReviews(reviewIds, action) {
    const count = reviewIds.length;
    const actionText = action === 'accept' ? 'accept' : 'reject';
    if (!window.confirm(`Are you sure you want to ${actionText} ${count} attribute review(s)?${action === 'accept' ? ' This will add the attributes to the plants.' : ''}`)) {
      return;
    }
    
    setResolvingAttributeReview('bulk');
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No admin token found');
        return;
      }
      
      const endpoint = action === 'accept' 
        ? `${API_BASE_URL}/plants/attribute-reviews/bulk-accept`
        : `${API_BASE_URL}/plants/attribute-reviews/bulk-reject`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reviewIds }),
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`Successfully ${action === 'accept' ? 'accepted' : 'rejected'} ${data.accepted || data.rejected || count} attribute review(s)!`);
        // Refresh reviews list
        fetchAttributeReviews();
        setSelectedAttributeReviews(new Set()); // Clear selection
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        alert(`Failed to resolve attribute reviews: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error resolving bulk attribute reviews:', error);
      alert(`Failed to resolve attribute reviews: ${error.message || 'Network error'}`);
    } finally {
      setResolvingAttributeReview(null);
    }
  }

  async function resolveSelectedAttributeReviewsForPlant(plantReviewIds, action) {
    const selectedIds = (Array.isArray(plantReviewIds) ? plantReviewIds : []).filter(id => selectedAttributeReviews.has(id));
    if (selectedIds.length === 0) return;
    const count = selectedIds.length;
    const actionText = action === 'accept' ? 'accept' : 'reject';
    if (!window.confirm(`Are you sure you want to ${actionText} ${count} selected review(s) for this plant?${action === 'accept' ? ' This will add the attributes to the plant.' : ''}`)) {
      return;
    }

    setResolvingAttributeReview('bulk');
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No admin token found');
        return;
      }

      const endpoint = action === 'accept'
        ? `${API_BASE_URL}/plants/attribute-reviews/bulk-accept`
        : `${API_BASE_URL}/plants/attribute-reviews/bulk-reject`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reviewIds: selectedIds }),
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Successfully ${action === 'accept' ? 'accepted' : 'rejected'} ${data.accepted || data.rejected || count} attribute review(s)!`);
        await fetchAttributeReviews();
        // Only clear the ids that were just resolved; keep other plant selections.
        setSelectedAttributeReviews(prev => {
          const next = new Set(prev);
          selectedIds.forEach(id => next.delete(id));
          return next;
        });
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        alert(`Failed to resolve attribute reviews: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error resolving bulk attribute reviews:', error);
      alert(`Failed to resolve attribute reviews: ${error.message || 'Network error'}`);
    } finally {
      setResolvingAttributeReview(null);
    }
  }
  
  async function resolveAttributeReview(reviewId, action) {
    if (!window.confirm(`Are you sure you want to ${action} this attribute review?${action === 'accept' ? ' This will add the attribute to the plant.' : ''}`)) {
      return;
    }
    
    setResolvingAttributeReview(reviewId);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No admin token found');
        return;
      }
      
      const endpoint = action === 'accept'
        ? `${API_BASE_URL}/plants/attribute-reviews/${reviewId}/accept`
        : `${API_BASE_URL}/plants/attribute-reviews/${reviewId}/reject`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`Attribute review ${action === 'accept' ? 'accepted' : 'rejected'} successfully!`);
        // Refresh reviews list
        fetchAttributeReviews();
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        alert(`Failed to resolve attribute review: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error resolving attribute review:', error);
      alert(`Failed to resolve attribute review: ${error.message || 'Network error'}`);
    } finally {
      setResolvingAttributeReview(null);
    }
  }

  async function resetRejectedAttributeReviews(attribute = null) {
    if (!window.confirm('This will reset REJECTED attribute reviews back to PENDING so you can review them again. Continue?')) {
      return;
    }

    setResolvingAttributeReview('reset-rejected');
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/plants/attribute-reviews/reset-rejected`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(attribute ? { attribute } : {}),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to reset rejected reviews');
      }

      alert(`Reset complete: ${data.reset || 0} review(s) moved back to pending.`);
      await fetchAttributeReviews();
    } catch (error) {
      console.error('Error resetting rejected attribute reviews:', error);
      alert(`Failed to reset rejected reviews: ${error.message || 'Network error'}`);
    } finally {
      setResolvingAttributeReview(null);
    }
  }

  async function enrichAttributeReviewsFromDb() {
    setEnrichingAttributeReviews(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No admin token found');
        return;
      }

      const attribute = attributeReviewFilter && attributeReviewFilter !== 'all' ? attributeReviewFilter : null;

      const response = await fetch(`${API_BASE_URL}/plants/attribute-reviews/enrich-from-db`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          limit: 150,
          attribute,
          onlyMissing: true
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || data.message || `Enrich failed (${response.status})`);
      }

      alert(data.message || `Enriched ${data.updated || 0} review(s).`);
      await fetchAttributeReviews();
    } catch (error) {
      console.error('Error enriching attribute reviews:', error);
      alert(`Failed to enrich reviews: ${error.message || 'Network error'}`);
    } finally {
      setEnrichingAttributeReviews(false);
    }
  }

  async function enrichAttributeReviewsFromExternal() {
    setEnrichingExternalAttributeReviews(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No admin token found');
        return;
      }

      const attribute = attributeReviewFilter && attributeReviewFilter !== 'all' ? attributeReviewFilter : null;

      const response = await fetch(`${API_BASE_URL}/plants/attribute-reviews/enrich-from-external`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          limit: 50,
          attribute,
          onlyMissing: true
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || data.message || `Enrich failed (${response.status})`);
      }

      alert(data.message || `Enriched ${data.updated || 0} review(s).`);
      await fetchAttributeReviews();
    } catch (error) {
      console.error('Error enriching attribute reviews from external sources:', error);
      alert(`Failed to enrich from external sources: ${error.message || 'Network error'}`);
    } finally {
      setEnrichingExternalAttributeReviews(false);
    }
  }

  function stopAutoEnrichExternal() {
    stopAutoEnrichExternalRef.current = true;
  }

  async function autoEnrichAttributeReviewsFromExternal() {
    if (!window.confirm('This will keep enriching external evidence in batches until none remain (processed = 0). Continue?')) {
      return;
    }

    setAutoEnrichingExternalAttributeReviews(true);
    setAutoEnrichExternalProgress({ batches: 0, processed: 0, updated: 0, lastMessage: '' });
    stopAutoEnrichExternalRef.current = false;

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No admin token found');
        return;
      }

      const attribute = attributeReviewFilter && attributeReviewFilter !== 'all' ? attributeReviewFilter : null;

      // Ask for a large batch; backend will clamp (prod <= 25, local <= 200).
      const requestedLimit = 200;
      let batches = 0;
      let processedTotal = 0;
      let updatedTotal = 0;

      // Safety cap to avoid infinite loops if something unexpected happens.
      const maxBatches = 500;

      while (!stopAutoEnrichExternalRef.current && batches < maxBatches) {
        const response = await fetch(`${API_BASE_URL}/plants/attribute-reviews/enrich-from-external`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            limit: requestedLimit,
            attribute,
            onlyMissing: true
          })
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          // Backoff for rate limiting or transient upstream errors.
          if (response.status === 429 || response.status === 503) {
            setAutoEnrichExternalProgress((prev) => ({
              ...prev,
              lastMessage: data?.message || data?.error || `Rate limited (${response.status}). Waiting 5s then continuing…`
            }));
            await sleep(5000);
            continue;
          }

          throw new Error(data.error || data.message || `Enrich failed (${response.status})`);
        }

        const processed = Number(data.processed || 0);
        const updated = Number(data.updated || 0);

        batches += 1;
        processedTotal += processed;
        updatedTotal += updated;

        setAutoEnrichExternalProgress({
          batches,
          processed: processedTotal,
          updated: updatedTotal,
          lastMessage: data.message || ''
        });

        // Stop condition: endpoint returns processed=0 when nothing remains.
        if (processed === 0) break;

        // Small pause so we don't hammer external services.
        await sleep(350);
      }

      if (stopAutoEnrichExternalRef.current) {
        alert(`Stopped. Auto-enrich ran ${batches} batch(es), updated ${updatedTotal} review(s).`);
      } else {
        alert(`Done. Auto-enrich ran ${batches} batch(es), updated ${updatedTotal} review(s).`);
      }

      await fetchAttributeReviews();
    } catch (error) {
      console.error('Error auto-enriching attribute reviews from external sources:', error);
      alert(`Auto-enrich failed: ${error.message || 'Network error'}`);
    } finally {
      setAutoEnrichingExternalAttributeReviews(false);
      stopAutoEnrichExternalRef.current = false;
    }
  }

  // Only show loading spinner if we're on a tab that uses the main loading state
  // UserManagement has its own loading state
  if (loading && activeTab !== 'users' && activeTab !== 'editorial' && activeTab !== 'landscaping-qa') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-900 to-black text-white px-6 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-purple-600 p-2 rounded-lg">
              <Shield size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold">Admin Portal</h1>
              <p className="text-xs text-purple-200">PlantADay Administration</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/profile"
              className="px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <User size={16} />
              My Profile
            </a>
            <button
              onClick={() => {
                if (activeTab === 'users') {
                  loadAllUsers();
                } else if (activeTab === 'plants') {
                  fetchPendingPlants();
                } else if (activeTab === 'designers') {
                  fetchDesigners();
                } else if (activeTab === 'attributes') {
                  fetchAttributeReviews();
                } else if (activeTab === 'corrections') {
                  fetchDataCorrections();
                } else if (activeTab === 'editorial') {
                  setLoading(false);
                } else if (activeTab === 'landscaping-qa') {
                  setLoading(false);
                }
              }}
              className="px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <div className="text-right hidden md:block">
              <p className="text-sm font-medium">{adminData?.email || 'Admin'}</p>
              <p className="text-xs text-purple-200">Administrator</p>
            </div>
            <button
              onClick={onLogout}
              className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => {
                setActiveTab('users');
                // UserManagement component handles its own loading
              }}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'users'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="inline mr-2" size={16} />
              User Management
            </button>
            <button
              onClick={() => {
                setActiveTab('plants');
                fetchPendingPlants();
              }}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'plants'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Leaf className="inline mr-2" size={16} />
              Pending Plants
              {pendingPlants.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-orange-500 text-white text-xs rounded-full">
                  {pendingPlants.length}
                </span>
              )}
            </button>
            <button
              onClick={() => {
                setActiveTab('corrections');
                fetchDataCorrections();
              }}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'corrections'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <AlertTriangle className="inline mr-2" size={16} />
              Data Corrections
              {dataCorrections.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {dataCorrections.length}
                </span>
              )}
            </button>
            <button
              onClick={() => {
                setActiveTab('attributes');
                fetchAttributeReviews();
              }}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'attributes'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Tag className="inline mr-2" size={16} />
              Attribute Reviews
              {attributeReviews.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                  {attributeReviews.length}
                </span>
              )}
            </button>
            <button
              onClick={() => {
                setActiveTab('designers');
                fetchDesigners();
              }}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'designers'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="inline mr-2" size={16} />
              Designers
            </button>
            <button
              onClick={() => setActiveTab('gardeners')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'gardeners'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Sprout className="inline mr-2" size={16} />
              Gardeners
            </button>
            <button
              onClick={() => setActiveTab('editorial')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'editorial'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Megaphone className="inline mr-2" size={16} />
              Editorial (public site)
            </button>
            <button
              onClick={() => setActiveTab('landscaping-qa')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'landscaping-qa'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <MessageCircleQuestion className="inline mr-2" size={16} />
              Yard Q&amp;A
            </button>
          </nav>
        </div>

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <UserManagement adminToken={localStorage.getItem('adminToken')} />
        )}

        {activeTab === 'gardeners' && <AdminGardeners />}

        {activeTab === 'editorial' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <AdminEditorialContent />
          </div>
        )}

        {activeTab === 'landscaping-qa' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <AdminLandscapingQA />
          </div>
        )}

        {/* Data Corrections Tab */}
        {activeTab === 'corrections' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Data Quality Corrections</h2>
                <p className="mt-1 text-sm text-gray-500">
                  AI-detected discrepancies in plant data. Review and apply corrections to improve database accuracy.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={fetchDataCorrections}
                  disabled={loadingCorrections}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={16} className={loadingCorrections ? 'animate-spin' : ''} />
                  Refresh
                </button>
              </div>
            </div>
            
            {/* Bulk Attribute Scan Section */}
            <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Pre-populate Attribute Cache</h3>
              <p className="text-sm text-gray-600 mb-4">
                Run a one-time bulk scan to check all plants for common attributes and cache the results. 
                This will make future searches much faster since AI won't need to check each plant individually.
              </p>
              <BulkAttributeScan />
            </div>
            
            {/* Filter Tabs */}
            {dataCorrections.length > 0 && (
              <div className="mb-4 sm:mb-6 border-b border-gray-200 overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                <nav className="-mb-px flex space-x-2 sm:space-x-4 min-w-max">
                  <button
                    onClick={() => setCorrectionFilter('all')}
                    className={`pb-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                      correctionFilter === 'all'
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    All ({dataCorrections.length})
                  </button>
                  <button
                    onClick={() => setCorrectionFilter('category')}
                    className={`pb-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                      correctionFilter === 'category'
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Category ({dataCorrections.filter(c => c.field === 'category').length})
                  </button>
                  <button
                    onClick={() => setCorrectionFilter('hardiness_zones')}
                    className={`pb-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                      correctionFilter === 'hardiness_zones'
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Zones ({dataCorrections.filter(c => c.field === 'hardiness_zones').length})
                  </button>
                </nav>
              </div>
            )}

            {loadingCorrections ? (
              <div className="text-center py-12">
                <RefreshCw className="inline animate-spin text-purple-600" size={32} />
                <p className="mt-2 text-gray-500">Loading corrections...</p>
              </div>
            ) : dataCorrections.length === 0 ? (
              <div className="text-center py-12">
                <AlertTriangle className="inline text-gray-400" size={48} />
                <p className="mt-4 text-gray-500">No pending corrections</p>
                <p className="mt-1 text-sm text-gray-400">All plant data appears to be accurate!</p>
              </div>
            ) : (
              <div>
                {/* Bulk Actions Bar */}
                {selectedCorrections.size > 0 && (
                  <div className="mb-4 p-3 sm:p-4 bg-purple-50 border border-purple-200 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-purple-900">
                        {selectedCorrections.size} correction{selectedCorrections.size !== 1 ? 's' : ''} selected
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      <button
                        onClick={() => resolveBulkCorrections(Array.from(selectedCorrections), 'accept', true)}
                        disabled={resolvingCorrection === 'bulk'}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 min-h-[44px]"
                      >
                        <Check size={16} />
                        Accept & Apply All
                      </button>
                      <button
                        onClick={() => resolveBulkCorrections(Array.from(selectedCorrections), 'accept', false)}
                        disabled={resolvingCorrection === 'bulk'}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 min-h-[44px]"
                      >
                        <Check size={16} />
                        Accept Only All
                      </button>
                      <button
                        onClick={() => resolveBulkCorrections(Array.from(selectedCorrections), 'reject', false)}
                        disabled={resolvingCorrection === 'bulk'}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 min-h-[44px]"
                      >
                        <X size={16} />
                        Reject All
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Filter and Select All */}
                {(() => {
                  const filteredCorrections = correctionFilter === 'all' 
                    ? dataCorrections 
                    : dataCorrections.filter(c => c.field === correctionFilter);
                  
                  const filteredSelected = filteredCorrections.filter(c => selectedCorrections.has(c.id)).length;
                  const allFilteredSelected = filteredCorrections.length > 0 && filteredSelected === filteredCorrections.length;
                  
                  return (
                    <>
                      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2 border-b border-gray-200">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={allFilteredSelected}
                            onChange={toggleSelectAll}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <label className="text-sm font-medium text-gray-700">
                            Select All ({filteredCorrections.length})
                          </label>
                        </div>
                        {correctionFilter !== 'all' && (
                          <span className="text-xs text-gray-500">
                            Showing {correctionFilter === 'category' ? 'Category' : 'Hardiness Zones'} only
                          </span>
                        )}
                      </div>
                      
                      <div className="space-y-4">
                        {filteredCorrections.map((correction) => (
                  <div
                    key={correction.id}
                    className={`border rounded-lg p-3 sm:p-5 transition-colors ${
                      selectedCorrections.has(correction.id)
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={selectedCorrections.has(correction.id)}
                          onChange={() => toggleCorrectionSelection(correction.id)}
                          className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                            {correction.plant_name}
                          </h3>
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded self-start sm:self-auto">
                            {correction.field === 'category' ? 'Category' : 'Hardiness Zones'}
                          </span>
                        </div>
                        
                        {correction.botanical_name && (
                          <p className="text-sm text-gray-600 italic mb-3 break-words">
                            {correction.botanical_name}
                          </p>
                        )}
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                          <div className="bg-red-50 border border-red-200 rounded p-2 sm:p-3">
                            <p className="text-xs font-medium text-red-700 mb-1">Database Value</p>
                            <p className="text-sm text-gray-900 font-medium break-words">
                              {correction.database_value || '(empty)'}
                            </p>
                          </div>
                          <div className="bg-green-50 border border-green-200 rounded p-2 sm:p-3">
                            <p className="text-xs font-medium text-green-700 mb-1">AI Suggested Value</p>
                            <p className="text-sm text-gray-900 font-medium break-words">
                              {correction.ai_suggested_value}
                            </p>
                          </div>
                        </div>
                        
                        <div className="bg-blue-50 border border-blue-200 rounded p-2 sm:p-3 mb-3 sm:mb-4">
                          <p className="text-xs font-medium text-blue-700 mb-1">Reason</p>
                          <p className="text-sm text-gray-700 break-words">{correction.reason}</p>
                        </div>
                        
                        <p className="text-xs text-gray-500">
                          Detected: {new Date(correction.detected_at).toLocaleString()}
                        </p>
                        </div>
                      </div>
                      
                      <div className="flex flex-row sm:flex-col gap-2 sm:ml-6 sm:flex-shrink-0">
                        <button
                          onClick={() => resolveCorrection(correction.id, 'accept', true)}
                          disabled={resolvingCorrection === correction.id || resolvingCorrection === 'bulk'}
                          className="flex-1 sm:flex-none px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 min-h-[44px]"
                        >
                          <Check size={16} />
                          <span className="hidden sm:inline">Accept & Apply</span>
                          <span className="sm:hidden">Accept & Apply</span>
                        </button>
                        <button
                          onClick={() => resolveCorrection(correction.id, 'accept', false)}
                          disabled={resolvingCorrection === correction.id || resolvingCorrection === 'bulk'}
                          className="flex-1 sm:flex-none px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 min-h-[44px]"
                        >
                          <Check size={16} />
                          <span className="hidden sm:inline">Accept Only</span>
                          <span className="sm:hidden">Accept</span>
                        </button>
                        <button
                          onClick={() => resolveCorrection(correction.id, 'reject', false)}
                          disabled={resolvingCorrection === correction.id || resolvingCorrection === 'bulk'}
                          className="flex-1 sm:flex-none px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 min-h-[44px]"
                        >
                          <X size={16} />
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* Attribute Reviews Tab */}
        {activeTab === 'attributes' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Plant Attribute Reviews</h2>
                <p className="mt-1 text-sm text-gray-500">
                  AI-suggested plant attributes (deer resistant, colors, etc.) awaiting admin approval. Accept to add to plant database.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={enrichAttributeReviewsFromExternal}
                  disabled={loadingAttributeReviews || resolvingAttributeReview === 'bulk' || resolvingAttributeReview === 'reset-rejected' || enrichingExternalAttributeReviews || autoEnrichingExternalAttributeReviews}
                  className="px-4 py-2 bg-white border border-indigo-300 hover:bg-indigo-50 text-indigo-700 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
                  title="Fetch evidence from external sources (iNaturalist + POWO) and attach it to reviews. Runs in small batches to avoid timeouts."
                >
                  <Tag size={16} className={enrichingExternalAttributeReviews ? 'animate-pulse' : ''} />
                  {enrichingExternalAttributeReviews ? 'Enriching…' : 'Enrich (External)'}
                </button>
                {!autoEnrichingExternalAttributeReviews ? (
                  <button
                    onClick={autoEnrichAttributeReviewsFromExternal}
                    disabled={loadingAttributeReviews || resolvingAttributeReview === 'bulk' || resolvingAttributeReview === 'reset-rejected' || enrichingExternalAttributeReviews || autoEnrichingExternalAttributeReviews}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
                    title="Automatically repeat external enrichment batches until none remain (processed = 0)."
                  >
                    <Play size={16} />
                    Auto Enrich (External)
                  </button>
                ) : (
                  <button
                    onClick={stopAutoEnrichExternal}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                    title="Stop the auto enrichment loop after the current batch completes."
                  >
                    <X size={16} />
                    Stop
                  </button>
                )}
                <button
                  onClick={enrichAttributeReviewsFromDb}
                  disabled={loadingAttributeReviews || resolvingAttributeReview === 'bulk' || resolvingAttributeReview === 'reset-rejected' || enrichingAttributeReviews || autoEnrichingExternalAttributeReviews}
                  className="px-4 py-2 bg-white border border-purple-300 hover:bg-purple-50 text-purple-700 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
                  title="Annotate pending reviews with evidence from existing database fields (category/light/water/notes). Does not auto-accept/reject."
                >
                  <Tag size={16} className={enrichingAttributeReviews ? 'animate-pulse' : ''} />
                  {enrichingAttributeReviews ? 'Enriching…' : 'Enrich from DB'}
                </button>
                <button
                  onClick={() => resetRejectedAttributeReviews()}
                  disabled={loadingAttributeReviews || resolvingAttributeReview === 'bulk' || resolvingAttributeReview === 'reset-rejected'}
                  className="px-4 py-2 bg-white border border-red-300 hover:bg-red-50 text-red-700 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
                  title="Move rejected reviews back to pending so you can review them again"
                >
                  <AlertTriangle size={16} />
                  Reset Rejected
                </button>
                <button
                  onClick={fetchAttributeReviews}
                  disabled={loadingAttributeReviews}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={16} className={loadingAttributeReviews ? 'animate-spin' : ''} />
                  Refresh
                </button>
              </div>
            </div>
            {autoEnrichingExternalAttributeReviews && (
              <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded p-3">
                <p className="text-sm text-indigo-800 font-medium">
                  Auto external enrich running…
                </p>
                <p className="text-xs text-indigo-700 mt-1">
                  Batches: {autoEnrichExternalProgress.batches} • Processed: {autoEnrichExternalProgress.processed} • Updated: {autoEnrichExternalProgress.updated}
                  {autoEnrichExternalProgress.lastMessage ? ` • ${autoEnrichExternalProgress.lastMessage}` : ''}
                </p>
              </div>
            )}

            {/* Evidence filter */}
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Evidence filter:</span>
                <select
                  value={attributeReviewEvidenceFilter}
                  onChange={(e) => {
                    const next = e.target.value;
                    setAttributeReviewEvidenceFilter(next);
                    // Refetch with new server-side filter immediately
                    fetchAttributeReviews(next);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Filter reviews by where the evidence came from"
                >
                  <option value="all">All</option>
                  <option value="verified">Verified fields only</option>
                  <option value="ai_cache">AI cache only</option>
                  <option value="external">Has external evidence</option>
                  <option value="external_supports">External evidence supports</option>
                  <option value="external_inat">External: iNaturalist / Wikipedia</option>
                  <option value="external_powo">External: POWO</option>
                  <option value="none">No evidence yet</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Group by:</span>
                <select
                  value={attributeReviewGroupBy}
                  onChange={(e) => setAttributeReviewGroupBy(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Group the review list to make approval easier"
                >
                  <option value="plant">Plant</option>
                  <option value="attribute">Attribute</option>
                </select>
              </div>
              <div className="text-xs text-gray-500">
                Tip: run “Enrich from DB” and/or “Enrich (External)” first, then filter.
              </div>
            </div>
            
            {/* Bulk Attribute Scan Section */}
            <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Scan Plants for Attributes</h3>
              <p className="text-sm text-gray-600 mb-4">
                Run a bulk scan to check all plants for common attributes and colors. 
                Results will appear here for review before being added to the database.
              </p>
              <BulkAttributeScan />
            </div>
            
            {/* Filter Tabs - Group by attribute */}
            {attributeReviews.length > 0 && (
              <div className="mb-4 sm:mb-6 border-b border-gray-200 overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                <nav className="-mb-px flex space-x-2 sm:space-x-4 min-w-max">
                  <button
                    onClick={() => setAttributeReviewFilter('all')}
                    className={`pb-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                      attributeReviewFilter === 'all'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    All ({attributeReviews.length})
                  </button>
                  {Object.keys(attributeReviewStats).sort().map((attr) => {
                    const count = attributeReviewStats[attr]?.pending || 0;
                    if (count === 0) return null;
                    return (
                      <button
                        key={attr}
                        onClick={() => setAttributeReviewFilter(attr)}
                        className={`pb-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                          attributeReviewFilter === attr
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {attr.charAt(0).toUpperCase() + attr.slice(1)} ({count})
                      </button>
                    );
                  })}
                </nav>
              </div>
            )}

            {loadingAttributeReviews ? (
              <div className="text-center py-12">
                <RefreshCw className="inline animate-spin text-blue-600" size={32} />
                <p className="mt-2 text-gray-500">Loading attribute reviews...</p>
              </div>
            ) : attributeReviews.length === 0 ? (
              <div className="text-center py-12">
                <Tag className="inline text-gray-400" size={48} />
                <p className="mt-4 text-gray-500">No pending attribute reviews</p>
                <p className="mt-1 text-sm text-gray-400">Run a bulk scan to generate attribute suggestions for review.</p>
              </div>
            ) : (
              <div>
                {/* Bulk Actions Bar */}
                {selectedAttributeReviews.size > 0 && (
                  <div className="mb-4 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-blue-900">
                        {selectedAttributeReviews.size} review{selectedAttributeReviews.size !== 1 ? 's' : ''} selected
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      <button
                        onClick={() => resolveBulkAttributeReviews(Array.from(selectedAttributeReviews), 'accept')}
                        disabled={resolvingAttributeReview === 'bulk'}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 min-h-[44px]"
                      >
                        <Check size={16} />
                        Accept All
                      </button>
                      <button
                        onClick={() => resolveBulkAttributeReviews(Array.from(selectedAttributeReviews), 'reject')}
                        disabled={resolvingAttributeReview === 'bulk'}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 min-h-[44px]"
                      >
                        <X size={16} />
                        Reject All
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Filter and Select All */}
                {(() => {
                  const filteredReviews = attributeReviewFilter === 'all' 
                    ? attributeReviews 
                    : attributeReviews.filter(r => r.attribute === attributeReviewFilter);
                  
                  const filteredSelected = filteredReviews.filter(r => selectedAttributeReviews.has(r.id)).length;
                  const allFilteredSelected = filteredReviews.length > 0 && filteredSelected === filteredReviews.length;
                  
                  return (
                    <>
                      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2 border-b border-gray-200">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={allFilteredSelected}
                            onChange={toggleSelectAllAttributeReviews}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label className="text-sm font-medium text-gray-700">
                            Select All ({filteredReviews.length})
                          </label>
                        </div>
                        {attributeReviewFilter !== 'all' && (
                          <span className="text-xs text-gray-500">
                            Showing "{attributeReviewFilter}" only
                          </span>
                        )}
                      </div>
                      
                      <div className="space-y-4">
                        {attributeReviewGroupBy === 'plant' ? (() => {
                          const groups = new Map();
                          for (const review of filteredReviews) {
                            const plantKey = review.plant_id || `${review.plant_name || ''}__${review.botanical_name || ''}`;
                            if (!groups.has(plantKey)) {
                              groups.set(plantKey, {
                                plantKey,
                                plant_id: review.plant_id,
                                plant_name: review.plant_name,
                                botanical_name: review.botanical_name,
                                reviews: []
                              });
                            }
                            groups.get(plantKey).reviews.push(review);
                          }

                          const sortedGroups = Array.from(groups.values()).sort((a, b) => {
                            const an = (a.plant_name || a.botanical_name || '').toLowerCase();
                            const bn = (b.plant_name || b.botanical_name || '').toLowerCase();
                            return an.localeCompare(bn);
                          });

                          return (
                            <div className="space-y-4">
                              {sortedGroups.map((g) => {
                                const plantReviewIds = g.reviews.map(r => r.id);
                                const selectedCount = plantReviewIds.filter(id => selectedAttributeReviews.has(id)).length;
                                const allSelected = plantReviewIds.length > 0 && selectedCount === plantReviewIds.length;

                                return (
                                  <div key={g.plantKey} className="border border-gray-200 rounded-lg">
                                    <div className="px-3 sm:px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                        <div className="min-w-0">
                                          <div className="flex items-center gap-2 min-w-0">
                                            <input
                                              type="checkbox"
                                              checked={allSelected}
                                              onChange={() => toggleSelectAttributeReviewIds(plantReviewIds)}
                                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                                              title="Select all reviews for this plant"
                                            />
                                            <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                                              {g.plant_name || '(Unnamed plant)'}
                                            </h3>
                                            <div className="hidden sm:flex flex-wrap gap-2 items-center min-w-0">
                                              {getPlantEvidenceBadges(g.reviews).map(b => (
                                                <span
                                                  key={b.key}
                                                  className={`px-2 py-1 text-[11px] font-medium rounded border ${b.className} truncate max-w-[260px]`}
                                                  title={b.label}
                                                >
                                                  {b.label}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                          {g.botanical_name && (
                                            <p className="text-sm text-gray-600 italic mt-1 break-words">
                                              {g.botanical_name}
                                            </p>
                                          )}
                                          <div className="flex sm:hidden flex-wrap gap-2 items-center mt-2">
                                            {getPlantEvidenceBadges(g.reviews).map(b => (
                                              <span
                                                key={b.key}
                                                className={`px-2 py-1 text-[11px] font-medium rounded border ${b.className}`}
                                              >
                                                {b.label}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                        <div className="text-xs text-gray-600 flex flex-col sm:flex-row sm:items-center gap-2">
                                          <div className="flex items-center gap-2">
                                            <span className="px-2 py-1 bg-white border border-gray-200 rounded">
                                              {g.reviews.length} review{g.reviews.length !== 1 ? 's' : ''}
                                            </span>
                                            {selectedCount > 0 && (
                                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                                                {selectedCount} selected
                                              </span>
                                            )}
                                          </div>
                                          {selectedCount > 0 && (
                                            <div className="flex gap-2">
                                              <button
                                                onClick={() => resolveSelectedAttributeReviewsForPlant(plantReviewIds, 'accept')}
                                                disabled={resolvingAttributeReview === 'bulk'}
                                                className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 min-h-[36px]"
                                                title={allSelected ? "Accept all rows for this plant" : "Accept only the selected rows for this plant"}
                                              >
                                                <Check size={14} />
                                                {allSelected ? 'Accept all' : `Accept selected (${selectedCount})`}
                                              </button>
                                              <button
                                                onClick={() => resolveSelectedAttributeReviewsForPlant(plantReviewIds, 'reject')}
                                                disabled={resolvingAttributeReview === 'bulk'}
                                                className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 min-h-[36px]"
                                                title={allSelected ? "Reject all rows for this plant" : "Reject only the selected rows for this plant"}
                                              >
                                                <X size={14} />
                                                {allSelected ? 'Reject all' : `Reject selected (${selectedCount})`}
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    <div className="p-3 sm:p-5 space-y-4">
                                      {g.reviews.map((review) => (
                                        <div
                                          key={review.id}
                                          className={`border rounded-lg p-3 sm:p-5 transition-colors ${
                                            selectedAttributeReviews.has(review.id)
                                              ? 'border-blue-500 bg-blue-50'
                                              : 'border-gray-200 hover:border-blue-300'
                                          }`}
                                        >
                                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                            <div className="flex items-start gap-3 flex-1 min-w-0">
                                              <input
                                                type="checkbox"
                                                checked={selectedAttributeReviews.has(review.id)}
                                                onChange={() => toggleAttributeReviewSelection(review.id)}
                                                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                                              />
                                              <div className="flex-1 min-w-0">
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded self-start sm:self-auto">
                                                    {review.attribute}
                                                  </span>
                                                </div>
                                                
                                                <div className="bg-green-50 border border-green-200 rounded p-2 sm:p-3 mb-3 sm:mb-4">
                                                  <p className="text-xs font-medium text-green-700 mb-1">Suggested Attribute</p>
                                                  <p className="text-sm text-gray-900 font-medium break-words">
                                                    {review.attribute}
                                                  </p>
                                                </div>
                                                
                                                {review.reason && (
                                                  <div className="bg-blue-50 border border-blue-200 rounded p-2 sm:p-3 mb-3 sm:mb-4">
                                                    <p className="text-xs font-medium text-blue-700 mb-1">AI Reasoning</p>
                                                    <p className="text-sm text-gray-700 break-words">
                                                      {review.reason}
                                                    </p>
                                                  </div>
                                                )}

                                                {review.source_summary && (
                                                  <div className="bg-purple-50 border border-purple-200 rounded p-2 sm:p-3 mb-3 sm:mb-4">
                                                    <p className="text-xs font-medium text-purple-700 mb-1">Evidence (Verified fields vs AI cache)</p>
                                                    <div className="text-sm text-purple-900 break-words">
                                                      {renderTextWithLinks(review.source_summary)}
                                                    </div>
                                                  </div>
                                                )}
                                                
                                                <p className="text-xs text-gray-500">
                                                  Created: {new Date(review.created_at).toLocaleString()}
                                                </p>
                                              </div>
                                            </div>
                                            
                                            <div className="flex flex-row sm:flex-col gap-2 sm:ml-6 sm:flex-shrink-0">
                                              <button
                                                onClick={() => resolveAttributeReview(review.id, 'accept')}
                                                disabled={resolvingAttributeReview === review.id || resolvingAttributeReview === 'bulk'}
                                                className="flex-1 sm:flex-none px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 min-h-[44px]"
                                              >
                                                <Check size={16} />
                                                Accept
                                              </button>
                                              <button
                                                onClick={() => resolveAttributeReview(review.id, 'reject')}
                                                disabled={resolvingAttributeReview === review.id || resolvingAttributeReview === 'bulk'}
                                                className="flex-1 sm:flex-none px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 min-h-[44px]"
                                              >
                                                <X size={16} />
                                                Reject
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })() : (
                          filteredReviews.map((review) => (
                            <div
                              key={review.id}
                              className={`border rounded-lg p-3 sm:p-5 transition-colors ${
                                selectedAttributeReviews.has(review.id)
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                  <input
                                    type="checkbox"
                                    checked={selectedAttributeReviews.has(review.id)}
                                    onChange={() => toggleAttributeReviewSelection(review.id)}
                                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                                        {review.plant_name}
                                      </h3>
                                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded self-start sm:self-auto">
                                        {review.attribute}
                                      </span>
                                    </div>
                                    
                                    {review.botanical_name && (
                                      <p className="text-sm text-gray-600 italic mb-3 break-words">
                                        {review.botanical_name}
                                      </p>
                                    )}
                                    
                                    <div className="bg-green-50 border border-green-200 rounded p-2 sm:p-3 mb-3 sm:mb-4">
                                      <p className="text-xs font-medium text-green-700 mb-1">Suggested Attribute</p>
                                      <p className="text-sm text-gray-900 font-medium break-words">
                                        {review.attribute}
                                      </p>
                                    </div>
                                    
                                    {review.reason && (
                                      <div className="bg-blue-50 border border-blue-200 rounded p-2 sm:p-3 mb-3 sm:mb-4">
                                        <p className="text-xs font-medium text-blue-700 mb-1">AI Reasoning</p>
                                        <p className="text-sm text-gray-700 break-words">
                                          {review.reason}
                                        </p>
                                      </div>
                                    )}

                                    {review.source_summary && (
                                      <div className="bg-purple-50 border border-purple-200 rounded p-2 sm:p-3 mb-3 sm:mb-4">
                                        <p className="text-xs font-medium text-purple-700 mb-1">Evidence (Verified fields vs AI cache)</p>
                                        <div className="text-sm text-purple-900 break-words">
                                          {renderTextWithLinks(review.source_summary)}
                                        </div>
                                      </div>
                                    )}
                                    
                                    <p className="text-xs text-gray-500">
                                      Created: {new Date(review.created_at).toLocaleString()}
                                    </p>
                                  </div>
                                </div>
                                
                                <div className="flex flex-row sm:flex-col gap-2 sm:ml-6 sm:flex-shrink-0">
                                  <button
                                    onClick={() => resolveAttributeReview(review.id, 'accept')}
                                    disabled={resolvingAttributeReview === review.id || resolvingAttributeReview === 'bulk'}
                                    className="flex-1 sm:flex-none px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 min-h-[44px]"
                                  >
                                    <Check size={16} />
                                    Accept
                                  </button>
                                  <button
                                    onClick={() => resolveAttributeReview(review.id, 'reject')}
                                    disabled={resolvingAttributeReview === review.id || resolvingAttributeReview === 'bulk'}
                                    className="flex-1 sm:flex-none px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 min-h-[44px]"
                                  >
                                    <X size={16} />
                                    Reject
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* Designers Management Tab */}
        {activeTab === 'designers' && (
          <>
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Designer Management</h2>
              <p className="text-sm text-gray-600 mb-6">
                Manage which designers appear in the public "Find a Designer" directory. 
                Only designers with active public listings will be visible to consumers.
              </p>

              {loadingDesigners ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <p className="mt-4 text-gray-600">Loading designers...</p>
                </div>
              ) : designers.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-600">No designers found.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {designers.map((designer) => (
                    <div
                      key={designer.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {designer.business_name || designer.display_name || designer.full_name}
                            </h3>
                            {designer.is_active_public ? (
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                                ✓ Active
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">
                                Inactive
                              </span>
                            )}
                            {designer.verification_status === 'approved' ? (
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                                Verified
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full font-medium">
                                {designer.verification_status || 'Pending'}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                            <div>
                              <span className="font-medium">Name:</span> {designer.display_name || designer.full_name}
                            </div>
                            <div>
                              <span className="font-medium">Email:</span> {designer.email}
                            </div>
                            <div>
                              <span className="font-medium">City:</span> {designer.city || 'N/A'}
                            </div>
                            <div>
                              <span className="font-medium">Country:</span> {designer.country || 'N/A'}
                            </div>
                          </div>
                          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3">
                            <span className="text-sm text-gray-700">Accepting new projects</span>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={designerAcceptingFromProfile(designer.profile_data)}
                              disabled={processing}
                              onClick={() =>
                                toggleDesignerAcceptingProjects(
                                  designer.id,
                                  designerAcceptingFromProfile(designer.profile_data)
                                )
                              }
                              className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 ${
                                designerAcceptingFromProfile(designer.profile_data) ? 'bg-green-600' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  designerAcceptingFromProfile(designer.profile_data) ? 'translate-x-5' : 'translate-x-0.5'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                        <div className="ml-4">
                          <button
                            onClick={() => toggleDesignerActive(designer.id, designer.is_active_public)}
                            disabled={processing}
                            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                              designer.is_active_public
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            } disabled:opacity-50`}
                          >
                            {designer.is_active_public ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Pending Plants Tab */}
        {activeTab === 'plants' && (
          <>
            {/* Stats Card */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Pending Plants</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{pendingPlants.length}</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-lg">
                    <Leaf size={24} className="text-green-600" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Approved Plants</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{approvedPlants.length}</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Check size={24} className="text-blue-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="mb-4 flex gap-2">
              <button
                onClick={() => {
                  setPlantsViewMode('pending');
                  fetchPendingPlants();
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  plantsViewMode === 'pending'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Pending ({pendingPlants.length})
              </button>
              <button
                onClick={() => {
                  setPlantsViewMode('approved');
                  fetchApprovedPlants();
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  plantsViewMode === 'approved'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Approved ({approvedPlants.length})
              </button>
            </div>

            {/* Main Content */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-white">
                <h2 className="text-xl font-bold text-gray-900">
                  {plantsViewMode === 'pending' ? 'Pending Plant Submissions' : 'Approved Public Plants'}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {plantsViewMode === 'pending' 
                    ? 'Review AI-discovered plants before adding to the community database'
                    : 'Manage approved plants in the public database. You can disable them to remove from public view.'}
                </p>
              </div>

              {loading ? (
                <div className="text-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading {plantsViewMode === 'pending' ? 'pending' : 'approved'} plants...</p>
                </div>
              ) : (plantsViewMode === 'pending' ? pendingPlants : approvedPlants).length === 0 ? (
                <div className="text-center py-16 text-gray-500">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                    <Check size={32} className="text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {plantsViewMode === 'pending' ? 'All Caught Up!' : 'No Approved Plants'}
                  </h3>
                  <p>
                    {plantsViewMode === 'pending' 
                      ? 'No pending plant submissions at the moment.'
                      : 'No approved plants in the public database.'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {(plantsViewMode === 'pending' ? pendingPlants : approvedPlants).map((plant) => {
                    const plantData = typeof plant.plant_data === 'string' 
                      ? JSON.parse(plant.plant_data) 
                      : plant.plant_data || {};
                    return (
                      <div key={plant.id} className="p-6 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start gap-4">
                          {plant.image_url && (
                            <img
                              src={plant.image_url}
                              alt={plant.common_name}
                              className="w-24 h-24 object-cover rounded-lg"
                              onError={(e) => {
                                e.target.src = 'https://images.unsplash.com/photo-1466781783364-36c955e42a7f?w=800&h=600&fit=crop';
                              }}
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">{plant.common_name}</h3>
                                <p className="text-sm text-gray-600 italic">{plant.botanical_name}</p>
                                <p className="text-xs text-gray-500 mt-1">Category: {plant.category}</p>
                              </div>
                              {plantsViewMode === 'pending' ? (
                                <button
                                  onClick={() => {
                                    setSelectedPlant(plant);
                                    setPlantReviewNotes('');
                                    setMergeIntoPlantId('');
                                    setMakePublic(true);
                                  }}
                                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium"
                                >
                                  Review
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Disable "${plant.common_name}" from public view?`)) {
                                      disablePlant(plant.id);
                                    }
                                  }}
                                  disabled={processing}
                                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:bg-gray-400"
                                >
                                  Disable
                                </button>
                              )}
                            </div>
                            
                            {plant.description && (
                              <p className="text-sm text-gray-700 mt-2 line-clamp-2">{plant.description}</p>
                            )}
                            
                            {plantData.mature_size && (
                              <p className="text-xs text-gray-500 mt-1">Size: {plantData.mature_size}</p>
                            )}
                            
                            {plant.submitter_name && (
                              <p className="text-xs text-gray-500 mt-2">
                                Submitted by: {plant.submitter_name} ({plant.submitter_email})
                              </p>
                            )}
                            
                            <p className="text-xs text-gray-400 mt-2">
                              Submitted: {new Date(plant.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* Plant Review Modal */}
      {selectedPlant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Review Plant Submission</h3>
                <button
                  onClick={() => {
                    setSelectedPlant(null);
                    setPlantReviewNotes('');
                    setMergeIntoPlantId('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {selectedPlant.image_url && (
                <img
                  src={selectedPlant.image_url}
                  alt={selectedPlant.common_name}
                  className="w-full h-64 object-cover rounded-lg"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1466781783364-36c955e42a7f?w=800&h=600&fit=crop';
                  }}
                />
              )}

              <div>
                <h4 className="text-lg font-semibold text-gray-900">{selectedPlant.common_name}</h4>
                <p className="text-sm text-gray-600 italic">{selectedPlant.botanical_name}</p>
                <p className="text-xs text-gray-500 mt-1">Category: {selectedPlant.category}</p>
              </div>

              {selectedPlant.description && (
                <div>
                  <h5 className="text-sm font-semibold text-gray-700 mb-1">Description</h5>
                  <p className="text-sm text-gray-600">{selectedPlant.description}</p>
                </div>
              )}

              {(() => {
                const plantData = typeof selectedPlant.plant_data === 'string' 
                  ? JSON.parse(selectedPlant.plant_data) 
                  : selectedPlant.plant_data || {};
                return (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {plantData.mature_size && (
                      <div>
                        <span className="text-gray-500">Mature Size:</span>
                        <p className="font-medium">{plantData.mature_size}</p>
                      </div>
                    )}
                    {plantData.light_requirements && (
                      <div>
                        <span className="text-gray-500">Light:</span>
                        <p className="font-medium">{plantData.light_requirements}</p>
                      </div>
                    )}
                    {plantData.water_requirements && (
                      <div>
                        <span className="text-gray-500">Water:</span>
                        <p className="font-medium">{plantData.water_requirements}</p>
                      </div>
                    )}
                    {plantData.hardiness_zones && (
                      <div>
                        <span className="text-gray-500">Hardiness Zones:</span>
                        <p className="font-medium">{plantData.hardiness_zones}</p>
                      </div>
                    )}
                  </div>
                );
              })()}

              {selectedPlant.submitter_name && (
                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <p className="text-gray-600">
                    <span className="font-semibold">Submitted by:</span> {selectedPlant.submitter_name} ({selectedPlant.submitter_email})
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Review Notes
                </label>
                <textarea
                  value={plantReviewNotes}
                  onChange={(e) => setPlantReviewNotes(e.target.value)}
                  placeholder="Enter review notes (optional)..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 min-h-[100px]"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="makePublic"
                  checked={makePublic}
                  onChange={(e) => setMakePublic(e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="makePublic" className="text-sm text-gray-700">
                  Make plant public in community database
                </label>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Merge Into Existing Plant (Optional)
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  If this plant already exists, enter the existing plant ID to merge this submission into it.
                </p>
                <input
                  type="text"
                  value={mergeIntoPlantId}
                  onChange={(e) => setMergeIntoPlantId(e.target.value)}
                  placeholder="Enter existing plant ID..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                />
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                {plantsViewMode === 'pending' && (
                  <>
                    <button
                      onClick={() => approvePlant(selectedPlant.id)}
                      disabled={processing}
                      className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                    >
                      <Check size={18} />
                      Approve & Add
                    </button>
                    {mergeIntoPlantId.trim() && (
                      <button
                        onClick={() => mergePlant(selectedPlant.id)}
                        disabled={processing}
                        className="px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                      >
                        <GitMerge size={18} />
                        Merge
                      </button>
                    )}
                    <button
                      onClick={() => rejectPlant(selectedPlant.id)}
                      disabled={processing}
                      className="px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      <X size={18} />
                      Reject
                    </button>
                  </>
                )}
                {plantsViewMode === 'approved' && (
                  <button
                    onClick={() => disablePlant(selectedPlant.id)}
                    disabled={processing}
                    className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    <X size={18} />
                    Disable from Public
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


